import { identityApiRef, useApi } from "@backstage/core-plugin-api";
import { Card, Text } from "@backstage/ui";
import { SWA_GATE, SWA_OWNER_PAGE_PATH } from "@internal/backstage-plugin-avd-common";
import { makeStyles } from "@material-ui/core/styles";
import ComputerOutlinedIcon from "@material-ui/icons/ComputerOutlined";
import WebOutlinedIcon from "@material-ui/icons/WebOutlined";
import { ReactNode, useEffect, useState } from "react";

import { useEntitled } from "../identity/ownership";
import { PageHero, PageShell, TileGrid } from "../layout/pageShell";
import { MY_WEB_APPS_SUBTITLE, MY_WEB_APPS_TITLE } from "../swa/swaEntitlement";

import { firstName } from "./firstName";

// The portal's landing page, in the shared page shell (docs/app/frontend-system.md § The page shell).
// Product tiles lead; placeholders fill the row so a lone tile never floats in whitespace.

const COLUMNS = 4;
const SUGGESTION = "Your suggestion for an automation can go here…";

const useStyles = makeStyles((theme) => ({
	// Reserves room for the fixed "Powered by" footer (Root/PoweredByTtc.tsx), which is out of flow.
	grid: { marginBottom: "6rem" },
	tile: { height: "100%", minHeight: 132 },
	tileBody: { display: "grid", gap: theme.spacing(0.5), justifyItems: "start" },
	icon: { color: "var(--bui-fg-secondary)", marginBottom: theme.spacing(0.5) },
	placeholder: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		minHeight: 132,
		padding: theme.spacing(2.5),
		textAlign: "center",
		border: "1px dashed var(--bui-border-1)",
		borderRadius: "var(--bui-radius-3)",
	},
	// Row fillers only make sense in a multi-column grid; on a phone they would stack as empty boxes.
	slot: { opacity: 0.45, "@media (max-width: 640px)": { display: "none" } },
}));

/** The colleague's first name, once the profile resolves; undefined until then or without one. */
function useFirstName(): string | undefined {
	const identityApi = useApi(identityApiRef);
	const [name, setName] = useState<string>();
	useEffect(() => {
		let active = true;
		identityApi
			.getProfileInfo()
			.then((profile) => active && setName(firstName(profile.displayName)))
			.catch(() => {});
		return () => {
			active = false;
		};
	}, [identityApi]);
	return name;
}

function ProductTile(
	props: Readonly<{ href: string; title: string; icon: ReactNode; children: string }>,
): ReactNode {
	const classes = useStyles();
	return (
		<Card href={props.href} label={props.title} className={classes.tile}>
			<div className={classes.tileBody}>
				<span className={classes.icon} aria-hidden="true">
					{props.icon}
				</span>
				<Text as="h2" variant="title-x-small">
					{props.title}
				</Text>
				<Text as="p" variant="body-small" color="secondary">
					{props.children}
				</Text>
			</div>
		</Card>
	);
}

export const HomePage = () => {
	const classes = useStyles();
	const name = useFirstName();
	const { member: webApps } = useEntitled(SWA_GATE);

	// No feedback tile: feedback lives in the sidebar's utility block (AppNav.tsx), not in a slot.
	const tiles = [true, webApps, true].filter(Boolean).length; // My AVD, My web apps, suggestion
	const empty = (COLUMNS - (tiles % COLUMNS)) % COLUMNS;

	return (
		<PageShell>
			<PageHero
				eyebrow="IM Developer Portal"
				title={name ? `Welcome, ${name}.` : "Welcome."}
				meta={
					<Text variant="body-medium" color="secondary">
						Your self-service cloud workspace.
					</Text>
				}
			/>
			<TileGrid className={classes.grid}>
				<ProductTile
					href="/my-avd"
					title="My AVD"
					icon={<ComputerOutlinedIcon fontSize="large" />}
				>
					Set up, connect to, or remove your personal Windows desktop in the cloud.
				</ProductTile>
				{webApps ? (
					<ProductTile
						href={SWA_OWNER_PAGE_PATH}
						title={MY_WEB_APPS_TITLE}
						icon={<WebOutlinedIcon fontSize="large" />}
					>
						{MY_WEB_APPS_SUBTITLE}
					</ProductTile>
				) : null}
				<div className={classes.placeholder}>
					<Text as="p" color="secondary">
						{SUGGESTION}
					</Text>
				</div>
				{Array.from({ length: empty }, (_, i) => (
					<div
						key={`slot-${i}`}
						className={`${classes.placeholder} ${classes.slot}`}
						aria-hidden="true"
					/>
				))}
			</TileGrid>
		</PageShell>
	);
};
